import type { OnInit, Type } from '@angular/core';
import { Component, inject, signal } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { IonApp, IonRouterOutlet, NavController } from '@ionic/angular/standalone';
import { isMockDevToolsEnabled } from '@core/mocks/mock-devtools.config';
import { AuthService } from '@core/auth/services/auth.service';
import { Router } from '@angular/router';
import { ErrorHandlerService } from '@core/error-handler/services/error-handler.service';
import type { TokenInfo } from '@core/auth/models/auth.models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { TokenService } from '@core/auth/services/token.service';
import { AppError } from '@core/error-handler/models/error.model';
import { PersonStoreService } from '@core/stores/person-store.service';
import { TokenStoreService } from '@core/stores/token-store.service';
import { NotificationService } from '@core/notification-handler/services/notification.service';
import { DeepLinkService } from '@core/services/deep-link.service';
import { SignInFlowService } from '@core/auth/components/sign-in/services/sign-in-flow.service';
import { SplashScreen } from '@capacitor/splash-screen';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [IonApp, IonRouterOutlet, TranslateModule, NgComponentOutlet]
})
export class AppComponent implements OnInit {
  /**
   * Set asynchronously when mock DevTools are enabled so the mock panel chunk is not
   * loaded or linked from the main bundle in production builds.
   */
  protected readonly mockPanelComponentType = signal<Type<unknown> | null>(null);

  private tokenService = inject(TokenService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private errorHandler = inject(ErrorHandlerService);
  private translate = inject(TranslateService);
  private personStore = inject(PersonStoreService);
  private tokenStore = inject(TokenStoreService);
  private navCtrl = inject(NavController);
  private notificationService = inject(NotificationService);
  private deepLinkService = inject(DeepLinkService);
  private signInFlowService = inject(SignInFlowService);

  constructor() {
    let selectedLanguage = window.localStorage.getItem('dhigSelectedLanguage');
    let selectedLanguageCode = 'en';
    if (selectedLanguage && selectedLanguage !== '') {
      selectedLanguageCode = selectedLanguage.toString();
    }

    this.translate.setDefaultLang(selectedLanguageCode);
    this.translate.addLangs(['en', 'de', 'sk', 'cs', 'hu', 'pl', 'sr']);

    this.deepLinkService.initialize();

    if (isMockDevToolsEnabled()) {
      void import('@core/mocks/mock-panel/mock-panel.component').then((m) => {
        this.mockPanelComponentType.set(m.MockPanelComponent);
      });
    }
  }

  async ngOnInit(): Promise<void> {
    await this.initializeApp();
    // Splash screen will auto-hide after x seconds (configured in capacitor.config.ts)
    // But we can also hide it manually if app initialization completes before 2 seconds
    // This ensures smooth transition without white/black screens
    try {
      // Wait a bit to ensure splash shows for at least the minimum duration
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await SplashScreen.hide();
    } catch {
      // SplashScreen plugin not available in browser — expected
    }
  }

  private async initializeApp(): Promise<void> {
    // Wait for auth initialization to complete
    await this.authService.waitForInitialization();

    // Get the current authentication state once
    const isAuthenticated = await firstValueFrom(this.authService.isAuthenticated);

    if (!isAuthenticated) {
      // Allow access to public pages without authentication
      const isPublicPage =
        this.router.url.includes('password-create') ||
        window.location.pathname.includes('password-create') ||
        this.router.url.startsWith('/validation') ||
        window.location.pathname.startsWith('/validation');

      // Only redirect to auth if not on auth page and not on a public page
      if (!this.router.url.startsWith('/auth') && !isPublicPage) {
        await this.navCtrl.navigateRoot('/auth');
      }
      return;
    }

    // Only load person data if authenticated and not already loaded
    if (!this.personStore.person()?.personId) {
      const token = await this.tokenService.getTokenFromStorage();
      if (!token) {
        this.errorHandler.handleError(new AppError('Token not found')).then();
        await this.navCtrl.navigateRoot('/auth');
        return;
      }

      const info = this.tokenService.readToken(token);
      const personId = info ? info.PersonId : null;

      this.tokenStore.updateTokenInfo(info ?? ({} as TokenInfo));

      if (personId) {
        this.personStore.updatePersonProps({ personId });
      }

      this.signInFlowService.verifyRestoredSession(this.tokenService.getPid());
    }
  }
}
